import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BbTableService, BbTableSelectOption, TableRow, TableConfig } from '../../../services/bb-table.service';
import { OverlayService } from '../../../services/overlay.service';
import { SapiService } from '../../../services/sapi.service';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { BbFilterConfiguration } from '../../shared/bb-filters/bb-filters.component';
import { QueryParameter } from '../../../interfaces/queryString';
import { AuthenticationService } from '../../../services/authentication.service';
import { ExportChannel } from 'app/interfaces/exportchannel';

@Component({
	selector: 'social-list',
	templateUrl: './social-list.component.html',
	styleUrls: ['./social-list.component.scss'],
	providers: [BbTableService]
})

export class SocialListComponent extends AbstractListComponent implements OnInit {

	filterConfig: BbFilterConfiguration;
	syncedCounts: { [channelid: string]: number } = {};
	deleteRight = false;
	rootPermission = false;
	createChannel = false;
	currentFilter = 'all';
	bigScreen = true;

	tableConfig: TableConfig;
	defaultOrder = ['id', 'profilePicture', 'remotePageTitle', 'remoteUrl', 'synced', 'automatedSync', 'createddate', 'createdBy', 'updateddate', 'updatedBy', 'status', 'buttons'];
	sortedOrder: string[] = [];

	constructor(protected bbTableService: BbTableService,
		protected sapiService: SapiService,
		protected router: Router,
		protected overlayService: OverlayService,
		protected translate: TranslateService,
		protected authenticationService: AuthenticationService) {

		super(bbTableService);

		this.authenticationService.hasPermission('delete').subscribe(hasRight => this.deleteRight = hasRight);
		this.authenticationService.hasPermission('root').subscribe((hasRight: boolean) => this.rootPermission = hasRight);

		this.listConfiguration = {
			name: 'exportchannel',
			basePath: '/library/social',
			entityTranslation: this.translate.instant('SOCIAL_CHANNEL').toLowerCase(),
			entitiesTranslation: this.translate.instant('SOCIAL_CHANNELS').toLowerCase()
		};

		this.filterConfig = {
			search: true,
			filters: [],
			presets: [{
				label: this.translate.instant('ALL'),
				value: 'all',
				filterSet: [],
			},
			{
				label: this.translate.instant('ACTIVE'),
				value: 'active',
				filterSet: [{
					filters: [{
						type: 'exportchannel',
						field: 'status',
						operator: 'is',
						value: 'active'
					}]
				}],
			},
			{
				label: this.translate.instant('INACTIVE'),
				value: 'inactive',
				filterSet: [{
					filters: [{
						type: 'exportchannel',
						field: 'status',
						operator: 'is',
						value: 'inactive'
					}]
				}],
			},
			{
				label: this.translate.instant('YOUTUBE'),
				value: 'youtube',
				filterSet: [{
					filters: [{
						type: 'exportchannel',
						field: 'exportsystemname',
						operator: 'is',
						value: 'YouTube'
					}]
				}],
			},
			{
				label: this.translate.instant('FACEBOOK'),
				value: 'facebook',
				filterSet: [{
					filters: [{
						type: 'exportchannel',
						field: 'exportsystemname',
						operator: 'is',
						value: 'Facebook'
					}]
				}],
			},
			{
				label: this.translate.instant('INSTAGRAM'),
				value: 'instagram',
				filterSet: [{
					filters: [{
						type: 'exportchannel',
						field: 'exportsystemname',
						operator: 'is',
						value: 'Instagram'
					}]
				}],
			}]
		};
	}

	ngOnInit() {
		this.bbTableService
			.setSelect(BbTableSelectOption.none)
			.setDefaultOrder(this.defaultOrder)
			.setLimit(15)
			.sort('createddate', 'desc')
			.linkToUrl();

		this.subscriptions.add(this.bbTableService.tableConfig.subscribe((c) => {
			this.tableConfig = c;
			this.setOrderColumns();
		}));

		super.ngOnInit();
		this.setOrderColumns();

		this.bigScreen = (window.screen.width >= 750 ? true : false);
	}

	// overridden
	redirectToDetails(row: TableRow) {
		if (row && row.data && row.data.id && row.data.subtype === 'Facebook') {
			this.router.navigate(['/library/social/facebook', row.data.id, 'general']);
		} else if (row && row.data && row.data.id && row.data.subtype === 'Instagram') {
			this.router.navigate(['/library/social/instagram', row.data.id, 'general']);
		} else {
			this.router.navigate(['/library/social', row.data.id, 'general']);
		}
	}

	rowsRetrieved(rows: TableRow[]) {
		rows.forEach(row => {
			const params: QueryParameter[] = [{
				name: 'q',
				values: ['exportchannelid:' + row.data.id]
			}];

			this.sapiService.query('mediaclip', params).subscribe(response => {
				this.syncedCounts[row.data.id] = response.totalAmount;
			});

			this.sapiService.get('exportchannel', row.data.id).subscribe(response => {
				const socialAccount: ExportChannel = response;
				if (socialAccount.remoteLogoImageUrl) {
					row.profilePicture = socialAccount.remoteLogoImageUrl;
				}
			});

		});
		super.rowsRetrieved(rows);
	}

	private setOrderColumns(): void {
		if (!this.tableConfig?.order) {
			this.sortedOrder = this.defaultOrder;
			return;
		}

		const sorted = [];

		// make sure column in config is still in default options
		this.tableConfig.order.forEach(columnName => {
			const found = this.defaultOrder.find(tc => tc === columnName);
			if (found) {
				sorted.push(columnName);
			}
		});

		// add missing (added later than the config created) add the end
		this.defaultOrder.forEach(defaultColumn => {
			const found = sorted.find(s => s === defaultColumn);
			if (!found) {
				sorted.push(defaultColumn);
			}
		});

		this.sortedOrder = sorted;
	}
}
